import {
	InspectorControls,
	__experimentalColorGradientSettingsDropdown as ColorGradientSettingsDropdown,
	__experimentalUseMultipleOriginColorsAndGradients as useMultipleOriginColorsAndGradients,
} from '@wordpress/block-editor';
import {
	PanelBody,
	RangeControl,
	FontSizePicker,
	__experimentalToolsPanel as ToolsPanel,
	__experimentalBorderBoxControl as BorderBoxControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';

import { fromColorPreset, toColorPreset } from '../../../utils/helpers';
import { usePresetPalette } from '../../../utils/preset-color-settings';

export default function StyleControls( {
	attributes,
	setAttributes,
	clientId,
} ) {
	const colorGradientSettings = useMultipleOriginColorsAndGradients();
	const palette = usePresetPalette();

	const {
		wrapperBgColor,
		wrapperBorder,
		wrapperBorderRadius,
		wrapperHeight,
		inputTextColor,
		inputPlaceholderColor,
		inputFontSize,
		buttonBgColor,
		buttonIconColor,
		buttonSize,
		buttonBorderRadius,
		buttonGap,
		chipBgColor,
		chipTextColor,
		chipFontSize,
		chipBorderRadius,
		chipGap,
		suggestionsMarginTop,
	} = attributes;

	const colorSettings = [
		{
			colorValue: wrapperBgColor,
			onColorChange: ( v ) =>
				setAttributes( { wrapperBgColor: v || '' } ),
			label: __( 'Input Wrapper Background', 'brandy-blocks' ),
			isShownByDefault: true,
			hasValue: () => !! wrapperBgColor,
			onDeselect: () => setAttributes( { wrapperBgColor: '' } ),
		},
		{
			colorValue: inputTextColor,
			onColorChange: ( v ) =>
				setAttributes( { inputTextColor: v || '' } ),
			label: __( 'Text', 'brandy-blocks' ),
			isShownByDefault: true,
			hasValue: () => !! inputTextColor,
			onDeselect: () => setAttributes( { inputTextColor: '' } ),
		},
		{
			colorValue: inputPlaceholderColor,
			onColorChange: ( v ) =>
				setAttributes( { inputPlaceholderColor: v || '' } ),
			label: __( 'Placeholder', 'brandy-blocks' ),
			isShownByDefault: false,
			hasValue: () => !! inputPlaceholderColor,
			onDeselect: () =>
				setAttributes( { inputPlaceholderColor: '' } ),
		},
		{
			colorValue: buttonBgColor,
			onColorChange: ( v ) =>
				setAttributes( { buttonBgColor: v || '' } ),
			label: __( 'Button Background', 'brandy-blocks' ),
			isShownByDefault: true,
			hasValue: () => !! buttonBgColor,
			onDeselect: () => setAttributes( { buttonBgColor: '' } ),
		},
		{
			colorValue: buttonIconColor,
			onColorChange: ( v ) =>
				setAttributes( { buttonIconColor: v || '' } ),
			label: __( 'Button Icon', 'brandy-blocks' ),
			isShownByDefault: true,
			hasValue: () => !! buttonIconColor,
			onDeselect: () => setAttributes( { buttonIconColor: '' } ),
		},
		{
			colorValue: chipBgColor,
			onColorChange: ( v ) =>
				setAttributes( { chipBgColor: v || '' } ),
			label: __( 'Chip Background', 'brandy-blocks' ),
			isShownByDefault: false,
			hasValue: () => !! chipBgColor,
			onDeselect: () => setAttributes( { chipBgColor: '' } ),
		},
		{
			colorValue: chipTextColor,
			onColorChange: ( v ) =>
				setAttributes( { chipTextColor: v || '' } ),
			label: __( 'Chip Text', 'brandy-blocks' ),
			isShownByDefault: false,
			hasValue: () => !! chipTextColor,
			onDeselect: () => setAttributes( { chipTextColor: '' } ),
		},
	];

	/*
	 * The tools-panel dropdown is core's, so it matches a swatch by comparing the
	 * value string against the palette. Map around it the same way
	 * PresetColorSettings does for PanelColorSettings: the control sees the
	 * concrete colour, the attribute keeps `var:preset|color|<slug>`.
	 * `colorValue`/`onColorChange` rather than `value`/`onChange` is the only
	 * difference, which is why this maps here instead of using that component.
	 */
	const presetColorSettings = colorSettings.map( ( setting ) => ( {
		...setting,
		colorValue: fromColorPreset( setting.colorValue, palette ),
		onColorChange: ( next ) =>
			setting.onColorChange( toColorPreset( next, palette ) ),
	} ) );

	const resetAllColors = () => {
		setAttributes( {
			wrapperBgColor: '',
			inputTextColor: '',
			inputPlaceholderColor: '',
			buttonBgColor: '',
			buttonIconColor: '',
			chipBgColor: '',
			chipTextColor: '',
		} );
	};

	return (
		<InspectorControls group="styles">
			<ToolsPanel
				label={ __( 'Color', 'brandy-blocks' ) }
				resetAll={ resetAllColors }
				panelId={ clientId }
			>
				<ColorGradientSettingsDropdown
					settings={ presetColorSettings }
					panelId={ clientId }
					__experimentalIsRenderedInSidebar
					{ ...colorGradientSettings }
				/>
			</ToolsPanel>

			<PanelBody
				title={ __( 'Input Wrapper', 'brandy-blocks' ) }
				initialOpen={ false }
			>
				<RangeControl
					label={ __( 'Height', 'brandy-blocks' ) }
					value={ wrapperHeight }
					onChange={ ( v ) =>
						setAttributes( { wrapperHeight: v } )
					}
					min={ 36 }
					max={ 80 }
				/>
				<RangeControl
					label={ __( 'Border Radius', 'brandy-blocks' ) }
					value={ wrapperBorderRadius }
					onChange={ ( v ) =>
						setAttributes( { wrapperBorderRadius: v } )
					}
					min={ 0 }
					max={ 50 }
				/>
				<BorderBoxControl
					colors={ colorGradientSettings.colors }
					label={ __( 'Border', 'brandy-blocks' ) }
					onChange={ ( v ) =>
						setAttributes( { wrapperBorder: v || {} } )
					}
					value={ wrapperBorder }
					__experimentalIsRenderedInSidebar
				/>
			</PanelBody>

			<PanelBody
				title={ __( 'Input', 'brandy-blocks' ) }
				initialOpen={ false }
			>
				<FontSizePicker
					__next40pxDefaultSize
					value={ inputFontSize }
					onChange={ ( v ) =>
						setAttributes( { inputFontSize: v } )
					}
				/>
			</PanelBody>

			<PanelBody
				title={ __( 'Search Button', 'brandy-blocks' ) }
				initialOpen={ false }
			>
				<RangeControl
					label={ __( 'Size', 'brandy-blocks' ) }
					value={ buttonSize }
					onChange={ ( v ) =>
						setAttributes( { buttonSize: v } )
					}
					min={ 28 }
					max={ 60 }
				/>
				<RangeControl
					label={ __( 'Border Radius', 'brandy-blocks' ) }
					value={ buttonBorderRadius }
					onChange={ ( v ) =>
						setAttributes( { buttonBorderRadius: v } )
					}
					min={ 0 }
					max={ 50 }
				/>
				<RangeControl
					label={ __( 'Gap from Input', 'brandy-blocks' ) }
					value={ buttonGap }
					onChange={ ( v ) =>
						setAttributes( { buttonGap: v } )
					}
					min={ 0 }
					max={ 16 }
				/>
			</PanelBody>

			<PanelBody
				title={ __( 'Suggestions', 'brandy-blocks' ) }
				initialOpen={ false }
			>
				<FontSizePicker
					__next40pxDefaultSize
					value={ chipFontSize }
					onChange={ ( v ) =>
						setAttributes( { chipFontSize: v } )
					}
				/>
				<RangeControl
					label={ __( 'Chip Border Radius', 'brandy-blocks' ) }
					value={ chipBorderRadius }
					onChange={ ( v ) =>
						setAttributes( { chipBorderRadius: v } )
					}
					min={ 0 }
					max={ 30 }
				/>
				<RangeControl
					label={ __( 'Gap Between Chips', 'brandy-blocks' ) }
					value={ chipGap }
					onChange={ ( v ) =>
						setAttributes( { chipGap: v } )
					}
					min={ 0 }
					max={ 20 }
				/>
				<RangeControl
					label={ __( 'Top Margin', 'brandy-blocks' ) }
					value={ suggestionsMarginTop }
					onChange={ ( v ) =>
						setAttributes( { suggestionsMarginTop: v } )
					}
					min={ 0 }
					max={ 32 }
				/>
			</PanelBody>
		</InspectorControls>
	);
}
